import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import AppShell from "../components/AppShell";
import { api, humanBytes, formatApiError } from "../lib/api";
import { Plus, FolderKanban, ArrowLeft, FileText, Trash2, ImagePlus, X, FileDown, Loader2 } from "lucide-react";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogTrigger,
} from "../components/ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from "../components/ui/alert-dialog";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import CustomerUsersPanel from "../components/CustomerUsersPanel";

export default function CustomerDetailPage() {
    const { customerId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const isAdmin = user?.role === "admin";
    const [customer, setCustomer] = useState(null);
    const [projects, setProjects] = useState(null);
    const [open, setOpen] = useState(false);
    const [form, setForm] = useState({ name: "", description: "" });
    const [submitting, setSubmitting] = useState(false);
    const [logoUploading, setLogoUploading] = useState(false);
    const logoInputRef = useRef(null);
    const [invoiceOpen, setInvoiceOpen] = useState(false);
    const [invoicePrice, setInvoicePrice] = useState("0.10");
    const [invoiceVat, setInvoiceVat] = useState("21");
    const [invoiceBusy, setInvoiceBusy] = useState(false);

    const load = async () => {
        try {
            const [c, p] = await Promise.all([
                api.get(`/customers/${customerId}`),
                api.get(`/customers/${customerId}/projects`),
            ]);
            setCustomer(c.data);
            setProjects(p.data);
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customerId]);

    const create = async (e) => {
        e.preventDefault();
        setSubmitting(true);
        try {
            await api.post(`/customers/${customerId}/projects`, {
                name: form.name,
                description: form.description || null,
            });
            toast.success("Project aangemaakt");
            setOpen(false);
            setForm({ name: "", description: "" });
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setSubmitting(false);
        }
    };

    const removeProject = async (id) => {
        try {
            await api.delete(`/projects/${id}`);
            toast.success("Project verwijderd");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const uploadLogo = async (file) => {
        if (!file) return;
        if (!file.type.startsWith("image/")) {
            toast.error("Logo moet een afbeelding zijn (PNG, JPG, SVG, WEBP).");
            return;
        }
        setLogoUploading(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            await api.post(`/customers/${customerId}/logo`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success("Logo geüpload");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setLogoUploading(false);
            if (logoInputRef.current) logoInputRef.current.value = "";
        }
    };

    const removeLogo = async () => {
        try {
            await api.delete(`/customers/${customerId}/logo`);
            toast.success("Logo verwijderd");
            load();
        } catch (e) {
            toast.error(formatApiError(e));
        }
    };

    const makeInvoice = async () => {
        setInvoiceBusy(true);
        try {
            const { data } = await api.post(
                `/customers/${customerId}/invoice`,
                {
                    price_per_gb_eur: parseFloat(invoicePrice) || 0,
                    vat_pct: parseFloat(invoiceVat) || 0,
                },
                { responseType: "blob" }
            );
            const url = URL.createObjectURL(data);
            const a = document.createElement("a");
            a.href = url;
            a.download = `factuur-${(customer?.name || "klant").replace(/[^a-z0-9-_ ]/gi, "")}-${new Date().toISOString().slice(0, 7)}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
            setInvoiceOpen(false);
            toast.success("Factuur gedownload");
        } catch (e) {
            toast.error("Factuur genereren mislukt");
        } finally {
            setInvoiceBusy(false);
        }
    };

    const pct = customer?.storage_quota_bytes
        ? Math.min(100, (customer.storage_used_bytes / customer.storage_quota_bytes) * 100)
        : 0;

    return (
        <AppShell>
            <div className="px-4 py-6 md:px-10 md:py-10 max-w-[1500px]" data-testid="customer-detail-page">
                <Link
                    to="/customers"
                    className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.18em] text-zinc-500 hover:text-zinc-900 mb-6"
                    data-testid="back-to-customers"
                >
                    <ArrowLeft className="w-3 h-3" />
                    Terug naar klanten
                </Link>

                {!customer ? (
                    <div className="h-32 bg-white border border-zinc-200 rounded-md animate-pulse" />
                ) : (
                    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6 mb-8 md:mb-10">
                        <div className="lg:col-span-2 flex flex-col sm:flex-row gap-4 sm:gap-6">
                            <div className="shrink-0">
                                <div
                                    className="w-20 h-20 md:w-24 md:h-24 rounded-md border border-zinc-200 bg-white overflow-hidden flex items-center justify-center relative group"
                                    data-testid="customer-logo"
                                >
                                    {customer.logo_url ? (
                                        <img
                                            src={customer.logo_url}
                                            alt={customer.name}
                                            className="w-full h-full object-contain p-1"
                                        />
                                    ) : (
                                        <div className="w-full h-full bg-[#09090b] text-white flex items-center justify-center font-display font-black text-3xl">
                                            {(customer.name || "?").slice(0, 1).toUpperCase()}
                                        </div>
                                    )}
                                    {isAdmin && customer.logo_url && (
                                        <button
                                            type="button"
                                            onClick={removeLogo}
                                            className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white border border-zinc-200 hover:bg-red-50 hover:text-red-600 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity shadow-sm"
                                            title="Logo verwijderen"
                                            data-testid="customer-logo-remove"
                                        >
                                            <X className="w-3 h-3" />
                                        </button>
                                    )}
                                </div>
                                {isAdmin && (
                                    <>
                                        <button
                                            type="button"
                                            onClick={() => logoInputRef.current?.click()}
                                            disabled={logoUploading}
                                            className="mt-2 w-full inline-flex items-center justify-center gap-1.5 text-xs px-2 py-1.5 border border-zinc-200 rounded-md hover:bg-zinc-50 text-zinc-700"
                                            data-testid="customer-logo-upload"
                                        >
                                            <ImagePlus className="w-3 h-3" />
                                            {logoUploading
                                                ? "Bezig…"
                                                : customer.logo_url
                                                    ? "Vervang"
                                                    : "Logo"}
                                        </button>
                                        <input
                                            ref={logoInputRef}
                                            type="file"
                                            accept="image/*"
                                            className="hidden"
                                            onChange={(e) => uploadLogo(e.target.files?.[0])}
                                            data-testid="customer-logo-input"
                                        />
                                    </>
                                )}
                            </div>
                            <div className="flex-1 min-w-0">
                                <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-2">Klant</div>
                                <h1 className="font-display text-3xl md:text-5xl tracking-tighter font-black leading-none break-words">
                                    {customer.name}
                                </h1>
                                {customer.company && (
                                    <div className="text-base text-zinc-600 mt-2">{customer.company}</div>
                                )}
                                {customer.contact_email && (
                                    <div className="text-sm font-mono text-zinc-500 mt-1">{customer.contact_email}</div>
                                )}
                                {customer.notes && (
                                    <p className="text-sm text-zinc-700 mt-4 max-w-xl whitespace-pre-wrap">
                                        {customer.notes}
                                    </p>
                                )}
                            </div>
                        </div>
                        <div className="bg-white border border-zinc-200 rounded-md p-6 flex flex-col gap-3">
                            <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">Opslag gebruikt</div>
                            <div className="font-display text-3xl font-black tracking-tighter leading-none">
                                {humanBytes(customer.storage_used_bytes)}
                            </div>
                            <div className="text-xs font-mono text-zinc-500">
                                van {humanBytes(customer.storage_quota_bytes)} ({pct.toFixed(1)}%)
                            </div>
                            <div className="h-1.5 bg-zinc-200 rounded-full overflow-hidden">
                                <div
                                    className={`h-full ${pct > 90 ? "bg-red-500" : pct > 75 ? "bg-amber-500" : "bg-[#0044FF]"}`}
                                    style={{ width: `${pct}%` }}
                                />
                            </div>
                            <div className="grid grid-cols-2 gap-3 mt-2 text-center">
                                <div className="border border-zinc-200 rounded-sm p-2">
                                    <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">
                                        Projecten
                                    </div>
                                    <div className="font-display text-xl font-bold mt-1">{customer.project_count}</div>
                                </div>
                                <div className="border border-zinc-200 rounded-sm p-2">
                                    <div className="text-xs uppercase tracking-[0.18em] text-zinc-500">
                                        Bestanden
                                    </div>
                                    <div className="font-display text-xl font-bold mt-1">{customer.file_count}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}

                {/* AI cost panel (admin only - cost data is hidden for customer role visually) */}
                {isAdmin && customer && (
                    <div className="bg-white border border-zinc-200 rounded-md p-6 mb-10" data-testid="customer-ai-cost">
                        <div className="flex items-center justify-between flex-wrap gap-4">
                            <div>
                                <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-1">
                                    AI-kost (gecumuleerd)
                                </div>
                                <div className="font-display text-3xl font-black tracking-tighter leading-none">
                                    €{((customer.ai_cost_usd || 0) * 0.92).toFixed(4)}
                                </div>
                                <div className="text-xs font-mono text-zinc-500 mt-1">
                                    ≈ ${(customer.ai_cost_usd || 0).toFixed(6)} USD
                                </div>
                            </div>
                            <div className="flex items-center gap-6 text-sm">
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                                        AI-aanvragen
                                    </div>
                                    <div className="font-display text-lg font-bold">
                                        {customer.ai_call_count || 0}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                                        Input tokens
                                    </div>
                                    <div className="font-mono text-sm">
                                        {Number(customer.ai_input_tokens || 0).toLocaleString("nl-BE")}
                                    </div>
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase tracking-[0.18em] text-zinc-500">
                                        Output tokens
                                    </div>
                                    <div className="font-mono text-sm">
                                        {Number(customer.ai_output_tokens || 0).toLocaleString("nl-BE")}
                                    </div>
                                </div>
                                <Button
                                    onClick={() => setInvoiceOpen(true)}
                                    variant="outline"
                                    className="rounded-md"
                                    data-testid="invoice-button"
                                >
                                    <FileDown className="w-4 h-4 mr-2" />
                                    Maak factuur
                                </Button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Invoice dialog (admin only) */}
                <Dialog open={invoiceOpen} onOpenChange={setInvoiceOpen}>
                    <DialogContent data-testid="invoice-dialog">
                        <DialogHeader>
                            <DialogTitle className="font-display tracking-tight text-2xl">
                                Factuur genereren
                            </DialogTitle>
                        </DialogHeader>
                        <div className="space-y-4">
                            <p className="text-sm text-zinc-600">
                                PDF met opslagkosten ({humanBytes(customer?.storage_used_bytes || 0)})
                                en AI-kosten (€{((customer?.ai_cost_usd || 0) * 0.92).toFixed(4)}) voor{" "}
                                <strong>{customer?.name}</strong>.
                            </p>
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <label className="text-xs uppercase tracking-[0.18em] text-zinc-500 mb-1.5 block">
                                        Prijs per GB (€/maand)
                                    </label>
                                    <Input
                                        type="number"
                                        step="0.01"
                                        min="0"
                                        value={invoicePrice}
                                        onChange={(e) => setInvoicePrice(e.target.value)}
                                        data-testid="invoice-price-input"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-[0.18em] text-zinc-500 mb-1.5 block">
                                        Btw (%)
                                    </label>
                                    <Input
                                        type="number"
                                        step="1"
                                        min="0"
                                        value={invoiceVat}
                                        onChange={(e) => setInvoiceVat(e.target.value)}
                                        data-testid="invoice-vat-input"
                                    />
                                </div>
                            </div>
                        </div>
                        <DialogFooter>
                            <Button
                                onClick={makeInvoice}
                                disabled={invoiceBusy}
                                className="bg-[#0044FF] hover:bg-[#0033CC] rounded-md"
                                data-testid="invoice-submit"
                            >
                                {invoiceBusy ? (
                                    <>
                                        <Loader2 className="w-4 h-4 mr-2 animate-spin" /> Bezig…
                                    </>
                                ) : (
                                    <>
                                        <FileDown className="w-4 h-4 mr-2" /> Download PDF
                                    </>
                                )}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* User management (admin only) */}
                {isAdmin && customer && (
                    <CustomerUsersPanel customerId={customer.id} customerName={customer.name} />
                )}

                <div className="flex items-end justify-between gap-3 flex-wrap mb-6">
                    <div>
                        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500 mb-1">
                            Projecten
                        </div>
                        <h2 className="font-display text-xl md:text-2xl tracking-tight font-bold">
                            {projects?.length || 0} project{projects?.length === 1 ? "" : "en"}
                        </h2>
                    </div>
                    <Dialog open={open} onOpenChange={setOpen}>
                        <DialogTrigger asChild>
                            <Button
                                className="bg-[#0044FF] hover:bg-[#0033CC] rounded-md"
                                data-testid="new-project-button"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                Nieuw project
                            </Button>
                        </DialogTrigger>
                        <DialogContent>
                            <DialogHeader>
                                <DialogTitle className="font-display tracking-tight text-2xl">
                                    Nieuw project
                                </DialogTitle>
                            </DialogHeader>
                            <form onSubmit={create} className="space-y-4" data-testid="new-project-form">
                                <div>
                                    <label className="text-xs uppercase tracking-[0.18em] text-zinc-500 mb-1.5 block">
                                        Naam *
                                    </label>
                                    <Input
                                        required
                                        value={form.name}
                                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                                        data-testid="new-project-name"
                                    />
                                </div>
                                <div>
                                    <label className="text-xs uppercase tracking-[0.18em] text-zinc-500 mb-1.5 block">
                                        Beschrijving
                                    </label>
                                    <Textarea
                                        rows={3}
                                        value={form.description}
                                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                                        data-testid="new-project-description"
                                    />
                                </div>
                                <DialogFooter>
                                    <Button
                                        type="submit"
                                        disabled={submitting}
                                        className="bg-[#0044FF] hover:bg-[#0033CC] rounded-md"
                                        data-testid="new-project-submit"
                                    >
                                        {submitting ? "Bezig…" : "Project aanmaken"}
                                    </Button>
                                </DialogFooter>
                            </form>
                        </DialogContent>
                    </Dialog>
                </div>

                {!projects ? (
                    <div className="h-32 bg-white border border-zinc-200 rounded-md animate-pulse" />
                ) : projects.length === 0 ? (
                    <div className="border border-dashed border-zinc-300 rounded-md py-16 text-center bg-white">
                        <FolderKanban className="w-10 h-10 text-zinc-300 mx-auto mb-4" />
                        <div className="font-display text-xl tracking-tight mb-2">Geen projecten</div>
                        <p className="text-sm text-zinc-500">
                            Maak een project om bestanden te organiseren.
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {projects.map((p) => (
                            <div
                                key={p.id}
                                className="bg-white border border-zinc-200 rounded-md p-6 hover:border-[#0044FF] hover:shadow-md hover:-translate-y-1 transition-all flex flex-col gap-3 cursor-pointer group"
                                data-testid={`project-card-${p.id}`}
                                onClick={() => navigate(`/projects/${p.id}`)}
                            >
                                <div className="flex items-start justify-between">
                                    <div className="w-10 h-10 bg-zinc-100 border border-zinc-200 rounded-sm flex items-center justify-center">
                                        <FolderKanban className="w-5 h-5 text-zinc-600 group-hover:text-[#0044FF]" />
                                    </div>
                                    <span className="text-[10px] font-mono uppercase text-zinc-500">
                                        {new Date(p.created_at).toLocaleDateString("nl-BE")}
                                    </span>
                                </div>
                                <div>
                                    <div className="font-display text-xl font-bold tracking-tight">{p.name}</div>
                                    {p.description && (
                                        <p className="text-sm text-zinc-600 mt-1 line-clamp-2">{p.description}</p>
                                    )}
                                </div>
                                <div className="flex items-center justify-between text-xs text-zinc-500 pt-3 border-t border-zinc-100">
                                    <span className="inline-flex items-center gap-1">
                                        <FileText className="w-3 h-3" />
                                        {p.file_count} bestanden
                                    </span>
                                    <span className="font-mono">{humanBytes(p.total_size_bytes)}</span>
                                </div>
                                <div onClick={(e) => e.stopPropagation()}>
                                    <AlertDialog>
                                        <AlertDialogTrigger asChild>
                                            <button
                                                className="text-zinc-400 hover:text-red-600 p-1.5 rounded hover:bg-zinc-100"
                                                data-testid={`project-delete-${p.id}`}
                                                aria-label="Project verwijderen"
                                            >
                                                <Trash2 className="w-4 h-4" />
                                            </button>
                                        </AlertDialogTrigger>
                                        <AlertDialogContent>
                                            <AlertDialogHeader>
                                                <AlertDialogTitle>Project verwijderen?</AlertDialogTitle>
                                                <AlertDialogDescription>
                                                    Alle bestanden binnen "{p.name}" worden permanent verwijderd.
                                                </AlertDialogDescription>
                                            </AlertDialogHeader>
                                            <AlertDialogFooter>
                                                <AlertDialogCancel>Annuleer</AlertDialogCancel>
                                                <AlertDialogAction
                                                    onClick={() => removeProject(p.id)}
                                                    className="bg-red-600 hover:bg-red-700"
                                                >
                                                    Verwijderen
                                                </AlertDialogAction>
                                            </AlertDialogFooter>
                                        </AlertDialogContent>
                                    </AlertDialog>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </AppShell>
    );
}
